import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Plus, Trash, PencilSimple, X, FloppyDisk } from "@phosphor-icons/react";

const field = "w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";
const CATS = ["Guide", "Checklist", "Cheat Sheet", "Template"];
const EMPTY = { title: "", description: "", category: "Guide", file_url: "", cover_image: "" };

export function ResourcesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/resources", { params: { lang: "en" } }).then((r) => setItems(r.data)).catch((e) => console.error("Failed to load resources:", e));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await api.put(`/admin/resources/${editId}`, form); toast.success("Resource updated"); }
      else { await api.post("/admin/resources", form); toast.success("Resource added"); }
      reset(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const edit = (r) => {
    setEditId(r.id);
    setForm({ title: r.title, description: r.description, category: r.category, file_url: r.file_url || "", cover_image: r.cover_image || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    try { await api.delete(`/admin/resources/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-resources-manager">
      <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-resource-form">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-ink">{editId ? "Edit resource" : "Add downloadable resource"}</h3>
          {editId && <button type="button" onClick={reset} className="flex items-center gap-1 text-xs text-slate-500 hover:text-ink"><X size={14} /> Cancel</button>}
        </div>
        <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title" className={field} />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
          {CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
        <textarea required rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="resource-desc" className={field} />
        <input placeholder="Cover image URL (optional)" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={field} />
        <ImageUpload value={form.cover_image} onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))} label="Upload cover image" testid="resource-cover-upload" />
        <input placeholder="File URL (optional)" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} className={field} />
        <ImageUpload value={form.file_url} onChange={(url) => setForm((f) => ({ ...f, file_url: url }))} accept="application/pdf" label="Upload PDF file" testid="resource-file-upload" />
        <button type="submit" data-testid="resource-submit" className="flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800">
          {editId ? <><FloppyDisk size={16} weight="bold" /> Save changes</> : <><Plus size={16} weight="bold" /> Add Resource</>}
        </button>
      </form>
      <div className="space-y-3">
        <h3 className="font-heading text-lg font-semibold text-ink">Resources ({items.length})</h3>
        {items.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-resource-${r.id}`}>
            <div><p className="font-heading text-sm font-semibold text-ink">{r.title}</p><p className="text-xs text-slate-400">{r.category}</p></div>
            <div className="flex items-center gap-3">
              <button onClick={() => edit(r)} data-testid={`edit-resource-${r.id}`} className="text-slate-400 hover:text-teal"><PencilSimple size={18} /></button>
              <button onClick={() => del(r.id)} data-testid={`delete-resource-${r.id}`} className="text-slate-400 hover:text-red-600"><Trash size={18} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No resources yet.</p>}
      </div>
    </div>
  );
}
